import { formatFullName } from 'platform/shared/health-apps';
import { formatDateLong } from 'platform/utilities/date';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { APP_URLS } from '../../utils/appUrls';
import { HIGH_DISABILITY_MINIMUM } from '../../utils/constants';
import { CONTACTS } from '../../utils/imports';
import AuthenticatedShortFormAlert from '../FormAlerts/AuthenticatedShortFormAlert';

const AuthProfileInformation = ({ user }) => {
  const { veteranFullName, veteranDateOfBirth, totalDisabilityRating } = user;

  const veteranDOB = useMemo(
    () => veteranDateOfBirth && formatDateLong(veteranDateOfBirth),
    [veteranDateOfBirth],
  );
  const veteranName = useMemo(
    () => formatFullName(veteranFullName, { includeMiddle: true }),
    [veteranFullName],
  );

  const ShortFormAlert = useMemo(
    () =>
      totalDisabilityRating >= HIGH_DISABILITY_MINIMUM ? (
        <AuthenticatedShortFormAlert />
      ) : null,
    [totalDisabilityRating],
  );

  const showNoDisabilityRatingMessage = totalDisabilityRating === 0;
  const showDisabilityRatingMessage = totalDisabilityRating > 0;

  return (
    <div className="vads-u-margin-top--2p5 vads-u-margin-bottom--2">
      {ShortFormAlert}

      <p>Confirm your information before you continue.</p>

      <va-card data-testid="hca-profile-card">
        <h2 className="vads-u-font-size--h3 vads-u-margin-top--0">
          Personal Information
        </h2>
        <ul className="no-bullets">
          <li className="vads-u-margin-y--1">
            <strong>Full name:</strong>{' '}
            <span
              className="vads-u-margin-bottom--1 dd-privacy-mask"
              data-dd-action-name="Veteran name"
            >
              {veteranName}
            </span>
          </li>
          {veteranDOB ? (
            <li className="vads-u-margin-y--1">
              <strong>Date of birth:</strong>{' '}
              <span
                className="dd-privacy-mask"
                data-dd-action-name="Date of birth"
              >
                {veteranDOB}
              </span>
            </li>
          ) : null}
          {showNoDisabilityRatingMessage ? (
            <li>
              <p>
                Our records show that you don’t have a VA service connected
                disability rating. This could be because a claim is currently in
                process or you have never applied for disability benefits.
              </p>
              <p>
                <va-link
                  text="Check the status of your claim"
                  href="/claim-or-appeal-status/"
                  external
                />
              </p>
              <p className="vads-u-margin-bottom--0">
                <va-link
                  text="Find out if you qualify for disability benefits"
                  href="/disability/eligibility/"
                  external
                />
              </p>
            </li>
          ) : null}
          {showDisabilityRatingMessage ? (
            <li>
              And our records show that you have a{' '}
              <strong>
                VA service-connected disability rating of{' '}
                <span
                  className="dd-privacy-mask"
                  data-dd-action-name="Disability rating"
                >
                  {totalDisabilityRating}%
                </span>
                .
              </strong>
            </li>
          ) : null}
        </ul>
      </va-card>

      <p>
        <strong>Note:</strong> If you need to update your personal information,
        call our VA benefits hotline at{' '}
        <va-telephone contact={CONTACTS.VA_BENEFITS} /> (
        <va-telephone contact={CONTACTS[711]} tty />
        ). We’re here Monday through Friday, 8:00 a.m. to 9:00 p.m.{' '}
        <dfn>
          <abbr title="Eastern Time">ET</abbr>
        </dfn>
        . Or you can call your VA medical center to get help changing your name
        on file with VA. Ask for the eligibility department.{' '}
        <va-link
          href={APP_URLS.facilities}
          text="Find a VA medical center near you"
        />
      </p>
    </div>
  );
};

AuthProfileInformation.propTypes = {
  user: PropTypes.shape({
    veteranFullName: PropTypes.object,
    veteranDateOfBirth: PropTypes.string,
    totalDisabilityRating: PropTypes.number,
  }).isRequired,
};

export default AuthProfileInformation;
